import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { formatCnpj, parseNfceKey } from '@domain/receipt'
import type { Receipt } from '@domain/receipt'
import { formatBRL } from '@shared/money/format'
import { Botao } from '@ui/glass/Botao'
import { Glass } from '@ui/glass/Glass'
import { Voltar } from '@ui/shell/Voltar'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { prepareImage } from '@ui/notas/prepare-image'
import { Scanner } from '@ui/produtos/Scanner'

/**
 * As notas fiscais: a captura e a lista. `Fase 8 - Nota fiscal e IA`.
 *
 * ## O QR vem PRIMEIRO, e é isso que a fase manda
 *
 * *"Leitura do QR Code da NFC-e quando disponível, que dá dado estruturado sem
 * OCR e sem custo de IA — sempre tentar primeiro."* O campo da chave fica ao
 * lado do da foto, e o que ele extrai — **CNPJ e data, certos** — viaja como
 * dica para a extração. São exatamente os dois campos que um cupom térmico
 * apagado faz o modelo errar.
 *
 * A chave é conferida pelo **dígito verificador** antes de virar dica: uma chave
 * com um dígito trocado é sintaticamente perfeita, e mandá-la ao modelo como
 * "isto é certo" seria pior do que não mandar nada.
 *
 * ## "Sem transação" é uma CAIXA DE ENTRADA, não um aviso
 *
 * *"Uma nota fiscal sempre deve estar vinculada a uma transação"*, mas subir sem
 * é legítimo: o sync roda uma vez por dia, e quem fotografa o cupom no
 * estacionamento não tem a transação ainda. O que não pode é a nota ficar assim
 * e ninguém lembrar.
 *
 * Por isso elas sobem para o topo, em bloco próprio e com contagem. Um selo
 * discreto no meio da lista seria a mesma informação com metade da chance de
 * alguém agir sobre ela.
 *
 * ## A foto é encolhida ANTES de subir
 *
 * Ver `prepare-image.ts` para os números e o motivo de cada um. Aqui só importa
 * o que a tela promete: **encolher nunca impede o envio**. Se o processamento
 * falhar, o original sobe — e a pessoa no corredor do mercado não fica sabendo
 * que existiu um otimizador.
 *
 * ## O QR agora é lido pela câmera
 *
 * A versão anterior desta nota dizia que não dava, porque `BarcodeDetector` não
 * existe no Safari. A parte específica continua verdadeira; o que estava errado
 * era tratá-la como o único caminho. `getUserMedia` mais uma biblioteca WASM lê
 * o QR no iPhone — ver `Scanner.tsx` para o custo disso, que foi medido antes de
 * ser aceito.
 *
 * Digitar os 44 dígitos continua funcionando, e continua sendo o caminho de
 * quando a câmera não colabora.
 *
 * A **foto** é `<input type="file" accept="image/*" capture>`: no celular abre a
 * câmera, no desktop o seletor de arquivo.
 */

export function Notas() {
  const { receiptRepository } = useFinanceRepositories()
  const navigate = useNavigate()

  const [notas, setNotas] = useState<Receipt[] | 'carregando' | 'falhou'>('carregando')
  const [chave, setChave] = useState('')
  const [lendoQr, setLendoQr] = useState(false)
  const [enviando, setEnviando] = useState<'preparando' | 'subindo' | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  /**
   * A nota que o ✕ marcou, esperando confirmação.
   *
   * Apagar aqui é definitivo — não há lixeira, e a imagem sai do Storage
   * junto. Um ✕ que apaga no primeiro toque, numa lista onde as linhas são
   * quase idênticas, transforma um deslize do polegar em foto perdida.
   */
  const [apagando, setApagando] = useState<Receipt | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const recarregar = useCallback(() => {
    void receiptRepository
      .list()
      .then(setNotas)
      .catch((e: unknown) => {
        console.error('Notas: a leitura falhou', e)
        setNotas('falhou')
      })
  }, [receiptRepository])

  useEffect(recarregar, [recarregar])

  const lida = chave.trim() === '' ? null : parseNfceKey(chave)
  const chaveInvalida = chave.trim() !== '' && lida === null

  async function enviar(file: File) {
    setEnviando('preparando')
    setErro(null)
    try {
      // Encolhe primeiro. Nunca lança: no pior caso devolve o original.
      const preparada = await prepareImage(file)
      setEnviando('subindo')
      const id = await receiptRepository.create(preparada.blob, {
        transactionId: null,
        // Só entra o que passou no dígito verificador. Ver o cabeçalho.
        extracted:
          lida === null
            ? null
            : {
                merchantName: null,
                merchantCnpj: lida.cnpj,
                issuedAt: null,
                totalCents: null,
                itemCount: 0,
                accessKey: lida.key,
              },
      })
      await navigate({ to: '/notas/$id', params: { id } })
    } catch (e: unknown) {
      console.error('Notas: o envio falhou', e)
      setErro('não deu para enviar a foto — tente de novo')
      setEnviando(null)
    }
  }

  return (
    <section>
      <div className="mb-2">
        <Voltar para="/">Início</Voltar>
      </div>
      <h1 className="titulo mb-4 text-2xl">Notas</h1>

      <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Nova nota">
        <label className="mb-3 block text-sm">
          <span className="block text-xs text-[var(--text-muted)]">
            Chave da NFC-e (opcional, e vale a pena)
          </span>
          <input
            value={chave}
            onChange={(e) => {
              setChave(e.target.value)
            }}
            inputMode="numeric"
            placeholder="44 dígitos do rodapé do cupom, ou o link do QR"
            className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
          />
        </label>

        {lendoQr && (
          <Scanner
            caso="qr"
            onFechar={() => {
              setLendoQr(false)
            }}
            onLer={(texto) => {
              // O conteúdo do QR é a URL da SEFAZ inteira; `parseNfceKey` já
              // sabe extrair os 44 dígitos dela.
              setChave(texto)
              setLendoQr(false)
            }}
          />
        )}

        {chaveInvalida && (
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            Essa chave não fecha no dígito verificador. Confira os números — a foto sozinha também
            funciona.
          </p>
        )}
        {lida !== null && (
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            {`Loja ${formatCnpj(lida.cnpj)} · emitida em ${lida.referenceMonth}. Isso vai junto com a foto.`}
          </p>
        )}

        <input
          ref={input}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-label="Foto do cupom"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file !== undefined) void enviar(file)
          }}
        />
        {/* **Os dois na mesma linha, e os dois como `Botao`.** O do QR era um
            link sublinhado de 12px ao lado de uma pílula, e em 375px eles se
            atropelavam — dois caminhos para a mesma coisa (registrar o cupom)
            com pesos diferentes por acidente, não por escolha. Fotografar é o
            principal e leva o tom da marca; o QR é a alternativa melhor quando
            existe, e fica ao lado com o mesmo alvo. */}
        <div className="flex flex-wrap items-center gap-2">
          <Botao
            tom="primario"
            type="button"
            disabled={enviando !== null}
            data-atalho-novo
            onClick={() => {
              input.current?.click()
            }}
          >
            {/* Os dois passos são nomeados porque são sentidos: preparar leva um
                instante numa foto de 12 MP, e subir leva o que o 4G do mercado
                der. "Enviando…" para os dois esconderia qual está demorando. */}
            {enviando === 'preparando'
              ? 'Preparando a foto…'
              : enviando === 'subindo'
                ? 'Enviando…'
                : 'Fotografar o cupom'}
          </Botao>
          <Botao
            type="button"
            onClick={() => {
              setLendoQr((atual) => !atual)
            }}
          >
            {lendoQr ? 'Fechar a câmera' : 'Ler o QR'}
          </Botao>
        </div>
        {erro !== null && (
          <p role="alert" className="mt-2 text-sm">
            {erro}
          </p>
        )}
      </Glass>

      {Array.isArray(notas) && semTransacao(notas).length > 0 && (
        <Glass level="frost" className="mb-4 p-4" as="section" aria-label="Notas sem transação">
          {/* O título leva a cor da marca: é o único bloco desta tela que pede
              alguma coisa, e sem isso ele fica com a mesma cara do "Todas as
              notas" logo abaixo — que foi exatamente a confusão registrada no
              comentário daquele cartão. */}
          <h2 className="mb-1 text-sm font-medium text-[var(--brand)]">
            {semTransacao(notas).length === 1
              ? '1 nota sem transação'
              : `${String(semTransacao(notas).length)} notas sem transação`}
          </h2>
          <p className="mb-2 text-xs text-[var(--text-muted)]">
            Entre em cada uma para escolher a compra. Enquanto isso, elas não viram detalhamento.
          </p>
          <ul aria-label="Sem transação">
            {semTransacao(notas).map((nota) => (
              <li
                key={nota.id}
                className="flex items-center border-b border-[var(--border)] last:border-b-0"
              >
                <LinhaDeNota nota={nota} />
              </li>
            ))}
          </ul>
        </Glass>
      )}

      <Glass level="frost" className="p-4" as="section" aria-label="Notas enviadas">
        {/*
          O título era só `aria-label` — invisível para quem enxerga.
          Consequência vista em produção: a mesma nota aparecia no cartão de
          "sem transação" e aqui, e os dois cartões pareciam iguais, um deles
          sem explicação nenhuma. Lia como duplicata, quando é a lista completa.
        */}
        <h2 className="mb-1 text-sm font-medium">Todas as notas</h2>
        {notas === 'carregando' && <p className="text-sm text-[var(--text-muted)]">Carregando…</p>}
        {notas === 'falhou' && <p className="text-sm">Não foi possível carregar as notas.</p>}
        {Array.isArray(notas) &&
          (notas.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">
              Nenhuma nota ainda. A primeira foto de cupom vira o detalhamento de uma compra.
            </p>
          ) : (
            <ul aria-label="Notas">
              {notas.map((nota) => (
                <li
                  key={nota.id}
                  className="flex items-center gap-1 border-b border-[var(--border)] last:border-b-0"
                >
                  <LinhaDeNota nota={nota} />

                  {/* Só o que ainda não virou dado. Uma nota confirmada gravou
                      breakdown, histórico de preço e apelidos aprendidos — e
                      quem recusa apagá-la é `firestore.rules`, não este `if`.
                      Aqui o `if` só evita oferecer o que vai falhar. */}
                  {nota.status !== 'confirmed' &&
                    (apagando?.id === nota.id ? (
                      <span className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          className="alvo rounded-lg bg-[var(--negative)] px-2 text-xs font-medium text-[var(--bg)]"
                          onClick={() => {
                            setApagando(null)
                            void receiptRepository
                              .remove(nota.id)
                              .then(() => {
                                setNotas((atuais) =>
                                  Array.isArray(atuais)
                                    ? atuais.filter((n) => n.id !== nota.id)
                                    : atuais,
                                )
                              })
                              .catch((e: unknown) => {
                                console.error('Notas: a nota não foi apagada', e)
                                setErro('Não deu para apagar a nota.')
                              })
                          }}
                        >
                          Apagar
                        </button>
                        <button
                          type="button"
                          className="alvo px-2 text-xs text-[var(--text-muted)]"
                          onClick={() => {
                            setApagando(null)
                          }}
                        >
                          Cancelar
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="alvo shrink-0 text-xs text-[var(--text-muted)]"
                        aria-label={`Apagar nota de ${nota.extracted?.merchantName ?? 'estabelecimento não identificado'}`}
                        onClick={() => {
                          setApagando(nota)
                        }}
                      >
                        ✕
                      </button>
                    ))}
                </li>
              ))}
            </ul>
          ))}
      </Glass>
    </section>
  )
}

/**
 * O status como SELO, e não como texto sublinhado.
 *
 * Na versão anterior a linha inteira era um `<Link>` com `underline`, então o
 * valor e o status apareciam sublinhados junto com o nome — três coisas com
 * cara de link, das quais só uma levava a algum lugar. Aqui o sublinhado fica
 * no nome, que é o que navega (mesma regra da linha de embalagem, decisão
 * 235f), e o status vira um selo com a cor do que ele pede.
 *
 * A cor não é enfeite: `revisar` e `falhou` esperam a pessoa e por isso têm
 * cor; `na fila`, `lendo…` e `confirmada` não pedem nada e ficam apagados. Uma
 * paleta que colorisse os cinco diria que os cinco são igualmente urgentes.
 */
function Selo({ status }: { status: Receipt['status'] }) {
  const cor =
    status === 'extracted'
      ? 'text-[var(--brand)]'
      : status === 'failed'
        ? 'text-[var(--negative)]'
        : 'text-[var(--text-faint)]'

  return <span className={`shrink-0 text-[11px] ${cor}`}>{ROTULO[status]}</span>
}

/** Uma nota na lista: o estabelecimento, o valor e o que falta nela. */
function LinhaDeNota({ nota }: { nota: Receipt }) {
  return (
    <Link
      to="/notas/$id"
      params={{ id: nota.id }}
      data-nav-linha
      className="flex min-w-0 flex-1 items-baseline gap-2 py-1 text-sm"
    >
      <span className="min-w-0 flex-1 truncate underline">
        {nota.extracted?.merchantName ?? 'Nota sem estabelecimento'}
      </span>
      <span className="shrink-0 tabular-nums text-[var(--text-muted)]">
        {nota.extracted?.totalCents == null ? '' : formatBRL(nota.extracted.totalCents)}
      </span>
      <Selo status={nota.status} />
    </Link>
  )
}

/**
 * As que ainda esperam uma compra. **`failed` fica de fora**: uma nota que não
 * deu para ler não tem o que vincular, e ela apareceria numa caixa de entrada
 * onde nenhuma ação resolve.
 */
function semTransacao(notas: Receipt[]): Receipt[] {
  return notas.filter((n) => n.transactionId === null && n.status !== 'failed')
}

/** O status em português. `processing` diz o que está acontecendo, não o nome
 * interno do estado — quem está esperando quer saber que a IA está lendo. */
const ROTULO: Record<Receipt['status'], string> = {
  uploaded: 'na fila',
  processing: 'lendo…',
  extracted: 'revisar',
  confirmed: 'confirmada',
  failed: 'falhou',
}
